import { useCallback, useEffect, useRef, useState } from 'react';
import { GetAdminChangelogItemsAPI } from '../../../../api/AdminAPI';

const ANY = { values: [], exclude: false };

// Os internos ficam escondidos por padrão: quase nunca são o que se procura
export const DEFAULT_CATALOG_FILTERS = {
  status: { values: ['internal', 'discarded'], exclude: true },
  sent: '',
  type: ANY,
  area: ANY,
};

const LIST_FILTERS = ['status', 'type', 'area'];

// { values, exclude } -> status=a,b&status_exclude=1 (formato de admin/changelog/items.php)
const toQueryParams = (filters) => LIST_FILTERS.reduce((params, key) => {
  const { values, exclude } = filters[key];
  if (!values.length) return params;
  return { ...params, [key]: values.join(','), ...(exclude ? { [`${key}_exclude`]: '1' } : {}) };
}, { sent: filters.sent });

const isDefaultFilters = (filters) => JSON.stringify(filters) === JSON.stringify(DEFAULT_CATALOG_FILTERS);
const SEARCH_DEBOUNCE_MS = 400;

/**
 * useCatalogItems
 *
 * Catálogo de novidades (admin/changelog/items.php) com busca, filtros e paginação.
 * Usado pela aba Catálogo e pelo seletor "Adicionar itens" do envio.
 *
 * @param {{ initialFilters?: Object, limit?: number, enabled?: boolean }} options
 *   enabled=false não carrega (ex.: seletor fechado); ao voltar a true recomeça dos filtros iniciais.
 */
function useCatalogItems({ initialFilters = DEFAULT_CATALOG_FILTERS, limit = 20, enabled = true } = {}) {
  const [filters, setFilters] = useState(initialFilters);
  const [searchInput, setSearchInputState] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit, pages: 1 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const searchTimer = useRef(null);
  const initialRef = useRef(initialFilters);

  useEffect(() => {
    if (!enabled) return;
    setFilters(initialRef.current);
    setSearchInputState('');
    setQ('');
    setPage(1);
  }, [enabled]);

  useEffect(() => () => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
  }, []);

  const setSearchInput = useCallback((value) => {
    setSearchInputState(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setQ(value.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    setIsLoading(true);
    setError(null);
    GetAdminChangelogItemsAPI({ q, ...toQueryParams(filters), page, limit }, (response) => {
      if (!active) return;
      setIsLoading(false);
      if (!response || response.success === false) {
        setError(response?.error || 'Erro ao carregar as novidades. Tente novamente.');
        return;
      }
      setItems(Array.isArray(response.items) ? response.items : []);
      setPagination(response.pagination || { total: 0, page, limit, pages: 1 });
    });
    return () => { active = false; };
  }, [enabled, q, filters, page, limit, reloadKey]);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  // Atualiza um item na página atual sem recarregar (depois de editar)
  const replaceItem = useCallback((updated) => {
    setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
  }, []);

  const hasActiveFilters = Boolean(q || !isDefaultFilters(filters));

  return {
    items, pagination, isLoading, error,
    filters, setFilter, searchInput, setSearchInput, setPage, hasActiveFilters,
    reload, replaceItem,
  };
}

export default useCatalogItems;
